import {type MouseEvent, useEffect, useRef, useState} from "react";
import {AccountAvatar} from "@/components/AccountAvatar";
import {openAccountModal} from "@/accountModal";
import {openConsultationModal} from "@/consultationModal";
import type {AuthUser} from "@/state/authStore";
import {useLandingAuth} from "@/state/authStore";

const HOME_URL = "/";
const CONSULTATION_URL = `${HOME_URL}#consultation`;

function displayName(user: AuthUser): string {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    if (fullName) {
        return fullName;
    }
    if (user.telegram_username) {
        return `@${user.telegram_username.replace(/^@/, "")}`;
    }
    return "Telegram";
}

function username(user: AuthUser): string {
    return user.telegram_username
        ? `@${user.telegram_username.replace(/^@/, "")}`
        : "Telegram-аккаунт";
}

export function PublicHeader({user}: {user: AuthUser | null}) {
    const clearSession = useLandingAuth((state) => state.clear);
    const [profileOpen, setProfileOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const profileWrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!profileWrapRef.current?.contains(event.target as Node)) {
                setProfileOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setProfileOpen(false);
                setMobileOpen(false);
            }
        };
        const handleResize = () => {
            if (window.innerWidth > 1099) {
                setMobileOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleResize);
        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        document.body.classList.toggle("landing-header-menu-open", mobileOpen);
        return () => document.body.classList.remove("landing-header-menu-open");
    }, [mobileOpen]);

    const logout = () => {
        clearSession();
        window.location.assign(HOME_URL);
    };

    const closeMobile = () => setMobileOpen(false);
    const openAccount = (event: MouseEvent<HTMLAnchorElement>, mode: "signin" | "register") => {
        event.preventDefault();
        setMobileOpen(false);
        setProfileOpen(false);
        void openAccountModal(mode, event.currentTarget);
    };
    const openConsultation = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setMobileOpen(false);
        setProfileOpen(false);
        void openConsultationModal(event.currentTarget);
    };

    return (
        <header className="landing-site-header" data-landing-site-header>
            <div className="landing-site-header__inner">
                <a className="landing-site-header__brand" href={HOME_URL} aria-label="Лидхолд — на главную">
                    <span className="landing-site-header__mark" aria-hidden="true"/>
                    <span className="landing-site-header__word">Лидхолд</span>
                </a>

                <nav className="landing-site-header__nav" aria-label="Основная навигация">
                    <a href="/help-center/">База знаний</a>
                    <a href="/referral.html">Партнёрам</a>
                    <a href="/cases.html">Кейсы</a>
                    <a href="/pricing.html">Цена</a>
                </nav>

                <div className="landing-header-actions">
                    <a className="landing-header-channel" href="https://t.me/leadhold">Канал</a>
                    {user ? (
                        <div className="landing-header-profile-wrap" ref={profileWrapRef}>
                            <button
                                className="landing-header-profile is-authenticated"
                                type="button"
                                aria-label={`Открыть меню аккаунта: ${displayName(user)}`}
                                aria-haspopup="menu"
                                aria-expanded={profileOpen}
                                onClick={() => {
                                    setMobileOpen(false);
                                    setProfileOpen((open) => !open);
                                }}
                            >
                                <AccountAvatar user={user}/>
                            </button>
                            <div
                                className="landing-header-profile-menu"
                                role="menu"
                                aria-label="Меню аккаунта"
                                hidden={!profileOpen}
                            >
                                <div className="landing-header-profile-identity">
                                    <strong>{displayName(user)}</strong>
                                    <span>{username(user)}</span>
                                </div>
                                <a
                                    className="landing-header-profile-menu-item"
                                    href="/cabinet/"
                                    role="menuitem"
                                    aria-current="page"
                                    onClick={() => setProfileOpen(false)}
                                >
                                    <span aria-hidden="true">↗</span>Личный кабинет
                                </a>
                                <button
                                    className="landing-header-profile-menu-item is-danger"
                                    type="button"
                                    role="menuitem"
                                    onClick={logout}
                                >
                                    <span aria-hidden="true">→</span>Выйти из аккаунта
                                </button>
                            </div>
                        </div>
                    ) : (
                        <a className="landing-header-access" href="/subscribe/" onClick={(event) => openAccount(event, "register")}>Зарегистрироваться</a>
                    )}
                    {!user && (
                        <a className="landing-header-consultation" href={CONSULTATION_URL} onClick={openConsultation}>
                            Получить консультацию
                        </a>
                    )}
                    <button
                        className="landing-site-header__menu-button"
                        type="button"
                        aria-label={mobileOpen ? "Закрыть меню" : "Открыть меню"}
                        aria-expanded={mobileOpen}
                        aria-controls="cabinet-header-menu"
                        onClick={() => {
                            setProfileOpen(false);
                            setMobileOpen((open) => !open);
                        }}
                    >
                        <span/>
                        <span/>
                    </button>
                </div>
            </div>

            <div className="landing-site-header__mobile" id="cabinet-header-menu" hidden={!mobileOpen}>
                <nav className="landing-site-header__mobile-inner" aria-label="Мобильная навигация">
                    <a href="/help-center/" onClick={closeMobile}>База знаний</a>
                    <a href="/referral.html" onClick={closeMobile}>Партнёрам</a>
                    <a href="/cases.html" onClick={closeMobile}>Кейсы</a>
                    <a href="/pricing.html" onClick={closeMobile}>Цена</a>
                    <a href="/cabinet/" aria-current={user ? "page" : undefined} onClick={user ? closeMobile : (event) => openAccount(event, "signin")}>
                        Личный кабинет
                    </a>
                    {!user && (
                        <>
                            <a className="landing-header-access" href="/subscribe/" onClick={(event) => openAccount(event, "register")}>
                                Зарегистрироваться
                            </a>
                            <a className="landing-header-consultation" href={CONSULTATION_URL} onClick={openConsultation}>
                                Получить консультацию
                            </a>
                        </>
                    )}
                </nav>
            </div>
        </header>
    );
}
